import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Dialog,
  Grid,
  TextField,
  Typography,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { capitalCase } from 'change-case';
import { isEmpty } from 'lodash';
import moment, { DurationInputArg2 } from 'moment';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityDefinitionWrapper,
  WrappedActivityDefinition,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ActivityDefinition';
import { WrappedGoal } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Goal';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedPractitioner } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import { WrappedPractitionerRole } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { WrappedRelatedPerson } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/RelatedPerson';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { useActivityDefinitions, usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import DatePickerMoment from 'src/components/DatePickerMoment';
import useComponentsConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useComponentsConfigData';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useObjectState from 'src/hooks/useObjectState';
import useTaskContext from '../../common/hook/useTaskContext';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import CancelButton from 'src/careflow/common/CancelButton';
import SelectSearchMember from 'src/careflow/common/SelectSearchMember';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';
import { createTask, updateTask } from '../../main/task.api';
import { findInvalidTaskOutputs, getDynamicValueExpressionByPath } from 'src/utils/fhir';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { WrappedCareTeam } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import {
  PatientWrapper,
  WrappedPatient,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import {
  CodeSystemConcept,
  Reference,
  TaskOutput,
  TaskInput,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import TaskInfoRibbon from './TaskInfoRibbon';
import TaskOutputComponent from './TaskOutput';
import TaskInputComponent from './TaskInput';
import ExpandableCard from 'src/components/common/ExpandableCard';
import {
  BreadCrumbTitle,
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import { onSuccess } from 'src/@types/crs/case';
import SearchMember, {
  MemberResourceTypeOption,
  SearchMemberProps,
} from 'src/careflow/care-coordination/case/detail/components/SearchMember/SearchMember';
import { ActionData } from 'src/careflow/care-coordination/case/goal/grid/GoalModal';
import GoalInfoRibbon from 'src/careflow/care-coordination/case/goal/grid/GoalInfoRibbon';

export type TaskOpenFrom = 'patient_details' | 'other';

export interface TaskModalProps extends CustomModalBasicProps {
  patientExternal?: WrappedPatient | null;
  onSuccessfulCreation: onSuccess;
  onSuccessfulEdit: onSuccess;
  carePlan?: WrappedCarePlan | null;
  goal?: WrappedGoal | null;
  taskToEdit?: WrappedTask;
  taskBasedOn?: WrappedTask | null;
  openFrom?: TaskOpenFrom;
}

export type TaskSelectedMemberOption = MemberResourceTypeOption;

export type TaskType = CodeSystemConcept;

export type TaskSelectedMemberValue =
  | WrappedPractitioner
  | WrappedCareTeam
  | WrappedPractitionerRole
  | WrappedPatient
  | WrappedOrganization
  | WrappedRelatedPerson;

export interface TaskFormState {
  isLoading: boolean;
  activityDefinition: WrappedActivityDefinition | null;
  taskType: TaskType | null;
  taskStatus: TaskStatus | null;
  taskPriority: TaskPriority | null;
  taskIntent: TaskIntent | null;
  taskReason: TaskReason | null;
  requester: TaskSelectedMemberValue | null;
  taskOwner: TaskSelectedMemberValue | null;
  requesterPerformer: TaskSelectedMemberValue | null;
  performer: TaskSelectedMemberValue | null;
  requestedStart: moment.Moment | null;
  requestedEnd: moment.Moment | null;
  requestedBy: string | null;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  description: string | null;
  comments: string | null;
  taskOutputs: TaskOutput[] | null;
  taskInputs: TaskInput[] | null;
}

export interface ErrorFieldState {
  taskStatus?: string | null;
  taskIntent?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  requestedStart?: string | null;
  requestedEnd?: string | null;
  description?: string | null;
  taskOutputs?: any;
  taskInputs?: any;
}

export type TaskStatus = ValueSetComposeIncludeConcept;

export type TaskPriority = ValueSetComposeIncludeConcept;

export type TaskIntent = ValueSetComposeIncludeConcept;

export type TaskReason = ValueSetComposeIncludeConcept;

export interface TaskSelectedMemberState {
  isSearchMemberOpen: boolean;
  isTaskOwnerOpen: boolean;
  inputSelected: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
  selectedRequester: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
  selectedTaskOwner: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
  selectedRequesterPerformer: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
  selectedPerformer: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
}

type TaskWizardSection = 'quickSetup' | 'assignmentIO' | 'annotationsComments';

const TaskModal = ({
  patientExternal,
  onSuccessfulCreation,
  onSuccessfulEdit,
  carePlan,
  taskToEdit,
  open,
  onClose,
  goal,
  taskBasedOn,
  openFrom,
}: TaskModalProps) => {
  const { i18n } = useLocales();

  const { basedOn, taskActivityDefinitionContext } = useTaskContext();

  const { enqueueSnackbar } = useSnackbar();

  const { componentsData, configurations } = useTenantConfigData();
  const { searchMemberOptions } = componentsData?.searchMember ?? {};
  const {
    activityDefintionsDisabledStatus = null,
    inputTypeFiles = [],
    outputTypeFiles = [],
  } = componentsData?.task ?? {};

  const componentsConfigData = useComponentsConfigData();

  const [priorityCode, setPriorityCode] = useState<string>();
  const [goalToDisplay, setGoalToDisplay] = useState<WrappedGoal | null>(null);
  const [activeSection, setActiveSection] = useState<TaskWizardSection>('quickSetup');
  const isInitializedRef = React.useRef(false);

  const [errors, updateErrorState] = useObjectState<ErrorFieldState>(errorsInitialState);

  const [
    {
      isSearchMemberOpen,
      isTaskOwnerOpen,
      selectedRequester,
      selectedTaskOwner,
      selectedRequesterPerformer,
      selectedPerformer,
      inputSelected,
    },
    updateSelectedMember,
  ] = useObjectState<TaskSelectedMemberState>(
    getInitialSelectedState(taskToEdit, configurations?.fhir)
  );

  const [patientRecord, { isFetching: isPatientLoading }] = usePatient(
    taskToEdit?.getPatientId?.() || taskBasedOn?.getPatientId?.(),
    {
      map: PatientWrapper,
      autofetch: !!(
        taskToEdit?.getPatientId?.() ||
        (taskBasedOn?.getPatientId?.() && !patientExternal)
      ),
    }
  );

  const patient: WrappedPatient | null = useMemo(() => {
    if (patientExternal) return patientExternal;
    if (patientRecord) return patientRecord;
    return null;
  }, [patientRecord, patientExternal]);

  const disabledStatus = useMemo(() => {
    if (!taskToEdit?.instantiatesCanonical || !activityDefintionsDisabledStatus) return false;
    const disabled =
      taskToEdit?.instantiatesCanonical && taskToEdit?.isActive()
        ? activityDefintionsDisabledStatus?.includes(taskToEdit.instantiatesCanonical)
        : false;
    return disabled;
  }, [taskToEdit, activityDefintionsDisabledStatus]);

  const taskFormConfig = useMemo(() => {
    if (!componentsConfigData) {
      return null;
    } else if (openFrom === 'patient_details') {
      return componentsConfigData.taskFormFromPatientDetailsConfig;
    }

    return componentsConfigData.taskFormConfig;
  }, [componentsConfigData, openFrom]);

  const [
    {
      isLoading,
      activityDefinition,
      taskType,
      taskStatus,
      taskPriority,
      taskIntent,
      taskReason,
      requester,
      startDate,
      endDate,
      description,
      comments,
      taskOwner,
      requesterPerformer,
      performer,
      requestedBy,
      requestedStart,
      requestedEnd,
      taskOutputs,
      taskInputs,
    },
    updateState,
  ] = useObjectState<TaskFormState>(
    getInitialState({
      taskToEdit: undefined,
      taskTypes: [],
      taskIntents: [],
      taskPriorities: [],
      taskStatuses: [],
      taskReasons: [],
      activityDefinitions: [],
      configurations: null,
    })
  );

  useEffect(() => {
    if (taskToEdit) {
      const { goalTaskAssociations } = window as any;

      if (goalTaskAssociations && taskToEdit.id) {
        const associatedGoal = goalTaskAssociations[taskToEdit.id];
        if (associatedGoal) {
          setGoalToDisplay(associatedGoal);
        } else {
          setGoalToDisplay(null);
        }
      } else {
        setGoalToDisplay(null);
      }
    } else {
      setGoalToDisplay(null);
    }
  }, [taskToEdit]);

  const [activityDefinitionsRecords] = useActivityDefinitions({
    map: ActivityDefinitionWrapper,
    filter: {
      _id: taskActivityDefinitionContext?.join(','),
      status: 'active',
    },
  });

  const compareByTitle = function (a, b) {
    if (a.title < b.title) {
      return -1;
    }
    if (a.title > b.title) {
      return 1;
    }
    return 0;
  };

  const activityDefinitions = useMemo(
    () =>
      activityDefinitionsRecords
        .reduce<WrappedActivityDefinition[]>((distinctActivityDefinitions, activityDefinition) => {
          if (distinctActivityDefinitions.find(({ title }) => title === activityDefinition.title))
            return distinctActivityDefinitions;

          return [...distinctActivityDefinitions, activityDefinition];
        }, [])
        .sort(compareByTitle),
    [activityDefinitionsRecords]
  );

  const {
    valueSets: [taskTypes, taskStatuses, taskIntents, taskPriorities, taskReasons],
  } = useValueSetsByIdentifiers([
    'ph-task-type-codes',
    'ph-task-status',
    'ph-task-intent',
    taskFormConfig?.priority?.valueSetIdentifier || 'ph-task-priority',
    'task-reason',
  ]);

  const taskPrioritiesOptions = useMemo(() => taskPriorities?.asList?.() ?? [], [taskPriorities]);

  const selectedTaskPriorityOption = useMemo(() => {
    if (!priorityCode) {
      return null;
    }

    const foundOption = taskPrioritiesOptions.find((item) => item.code === priorityCode);

    if (!foundOption) {
      return null;
    }

    return foundOption;
  }, [priorityCode, taskPrioritiesOptions]);

  function checkEditTaskPriority() {
    if (!taskToEdit) {
      return;
    }

    if (!taskToEdit.priority) {
      return;
    }

    const foundOption = taskPrioritiesOptions.find((item) => item.code === taskToEdit.priority);

    if (!foundOption) {
      return;
    }

    setPriorityCode(foundOption.code);
  }

  useEffect(() => {
    checkEditTaskPriority();
  }, [taskPrioritiesOptions, taskToEdit]);

  useEffect(() => {
    if (!open) {
      isInitializedRef.current = false;
      return;
    }

    if (!isInitializedRef.current) {
      setActiveSection('quickSetup');
      const initialState = getInitialState({
        taskToEdit,
        taskTypes: taskTypes?.asList?.(),
        taskIntents: taskIntents?.asList?.(),
        taskPriorities: taskPriorities?.asList?.(),
        taskStatuses: taskStatuses?.asList(),
        taskReasons: taskReasons?.asList?.(),
        activityDefinitions: activityDefinitions,
        configurations: configurations?.fhir,
      });
      updateState(initialState);
      updateErrorState(errorsInitialState);
      updateSelectedMember(getInitialSelectedState(taskToEdit, configurations?.fhir));
      isInitializedRef.current = true;
    }
  }, [
    open,
    taskToEdit,
    taskTypes,
    patient,
    taskStatuses,
    taskIntents,
    taskPriorities,
    taskReasons,
    configurations,
    updateState,
    updateErrorState,
    updateSelectedMember,
    activityDefinitions,
  ]);

  const getTaskNumber = useMemo(
    () =>
      taskToEdit
        ? taskToEdit?.getInternalNumber?.(
            configurations?.fhir?.taskInternalNumberIdentifierSystem
          ) ??
          taskToEdit?.getTaskNumberNAT(configurations?.fhir?.taskNATIdenitifierType) ??
          ''
        : '',
    [taskToEdit, configurations]
  );

  const getSectionErrors = (section: TaskWizardSection, errors: ErrorFieldState): string[] => {
    const sectionErrorMap: Record<TaskWizardSection, (keyof ErrorFieldState)[]> = {
      quickSetup: [
        'taskStatus',
        'taskIntent',
        'startDate',
        'endDate',
        'requestedStart',
        'requestedEnd',
      ],
      assignmentIO: ['taskOutputs', 'taskInputs'],
      annotationsComments: ['description'],
    };

    const sectionFields = sectionErrorMap[section];
    const sectionErrors: string[] = [];

    sectionFields.forEach((field) => {
      if (errors[field]) {
        const fieldLabels: Record<keyof ErrorFieldState, string> = {
          taskStatus: 'Status',
          taskIntent: 'Intent',
          startDate: 'Start Date',
          endDate: 'End Date',
          requestedStart: 'Requested Start',
          requestedEnd: 'Requested End',
          description: 'Description',
          taskOutputs: 'Task Outputs',
          taskInputs: 'Task Inputs',
        };

        if (Array.isArray(errors[field]) && errors[field].length > 0) {
          sectionErrors.push(fieldLabels[field] || field);
        } else if (!Array.isArray(errors[field]) && errors[field]) {
          sectionErrors.push(fieldLabels[field] || field);
        }
      }
    });

    return sectionErrors;
  };

  const hasSectionErrors = (section: TaskWizardSection, errors: ErrorFieldState): boolean =>
    getSectionErrors(section, errors).length > 0;

  const handleOnSave = async () => {
    const errors: ErrorFieldState = {};
    if (!description?.trim?.()?.length) errors.description = 'Must specify a description';
    if (!taskStatus) errors.taskStatus = 'Must select a valid status';
    if (!taskIntent) errors.taskIntent = 'Must select a valid intent';
    if (
      startDate &&
      endDate &&
      moment(endDate).startOf('day').isBefore(moment(startDate).startOf('day'))
    )
      errors.startDate = "End Date can't be before Start Date";
    if (startDate && !startDate?.isValid()) errors.startDate = 'Must specify a valid date';
    if (endDate && !endDate?.isValid()) errors.endDate = 'Must specify a valid date';
    if (
      requestedStart &&
      requestedEnd &&
      moment(requestedEnd).startOf('day').isBefore(moment(requestedStart).startOf('day'))
    )
      errors.requestedStart = "Requested End can't be before Requested Start";
    const taskOutputsHasEmptyType =
      Array.isArray(taskOutputs) && taskOutputs.length > 0
        ? findInvalidTaskOutputs(taskOutputs)
        : [];

    if (taskOutputsHasEmptyType.length > 0) {
      errors.taskOutputs = taskOutputsHasEmptyType;
    }

    const taskInputsHasEmptyType =
      Array.isArray(taskInputs) && taskInputs.length > 0 ? findInvalidTaskOutputs(taskInputs) : [];

    if (taskInputsHasEmptyType.length > 0) {
      errors.taskInputs = taskInputsHasEmptyType;
    }

    if (Object.keys(errors).length) {
      updateErrorState({ ...errors });
      return;
    }

    updateErrorState(errorsInitialState);
    const isCreating = !taskToEdit?.id;
    const { actionCall, payload: taskData, successfulMessage } = getTaskPayload(isCreating);
    updateState({ isLoading: true });

    const response = !taskToEdit?.id
      ? await createTask(taskData)
      : await updateTask(taskData, taskToEdit?.id as string);
    updateState({ isLoading: false });
    if (!response) {
      enqueueSnackbar('An error has occurred', { variant: 'error' });
      return;
    }

    enqueueSnackbar(successfulMessage);
    actionCall?.(response);
    onClose?.({}, 'backdropClick');
  };

  const filterTaskOuputs =
    taskOutputs && Array.isArray(taskOutputs) ? taskOutputs.filter((e) => !isEmpty(e?.type)) : null;
  const filterTaskInputs =
    taskInputs && Array.isArray(taskInputs) ? taskInputs.filter((e) => !isEmpty(e?.type)) : null;

  const getTaskPayload = (isCreating: boolean): ActionData => {
    let adjustedEndDate = endDate;
    if (startDate && endDate && endDate.isBefore(startDate)) {
      adjustedEndDate = moment(endDate).endOf('day');
    }

    let adjustedRequestedEnd = requestedEnd;
    if (requestedStart && requestedEnd && requestedEnd.isBefore(requestedStart)) {
      adjustedRequestedEnd = moment(requestedEnd).endOf('day');
    }

    const toDateOnlyISOString = (date: moment.Moment | null): string | null => {
      if (!date || !date.isValid()) return null;
      return date.clone().startOf('day').utc().toISOString();
    };

    return {
      payload: {
        description,
        comments,
        status: taskStatus?.code ?? null,
        intent: taskIntent?.code ?? null,
        priority: taskPriority?.code ?? null,
        taskType: taskType?.code ?? null,
        taskTypeDisplay: taskType?.display ?? null,
        taskReason: taskReason?.code ?? null,
        taskReasonDisplay: taskReason?.display ?? null,
        startDate: startDate?.toISOString() ?? null,
        endDate: adjustedEndDate?.toISOString() ?? null,
        requesterId: requester?.id ? `${requester?.resourceType}/${requester?.id}` : null,
        requesterDisplay: getOwnerDisplayLabel(requester) ?? '',
        ownerId: taskOwner?.id ? `${taskOwner?.resourceType}/${taskOwner?.id}` : null,
        ownerDisplay: getOwnerDisplayLabel(taskOwner) ?? null,
        requesterPerformerId: requesterPerformer?.id
          ? `${requesterPerformer?.resourceType}/${requesterPerformer?.id}`
          : null,
        requesterPerformerDisplay: getOwnerDisplayLabel(requesterPerformer),
        performerId: performer?.id ? `${performer?.resourceType}/${performer?.id}` : null,
        performerDisplay: getOwnerDisplayLabel(performer),
        requestedStart: toDateOnlyISOString(requestedStart),
        requestedEnd: toDateOnlyISOString(adjustedRequestedEnd),
        output: filterTaskOuputs && filterTaskOuputs?.length > 0 ? filterTaskOuputs : null,
        input: filterTaskInputs && filterTaskInputs?.length > 0 ? filterTaskInputs : null,
        identifier: taskToEdit?.identifier ? taskToEdit?.identifier : null,
        ...(isCreating
          ? {
              patientId: patient?.id,
              patientName: patient?.getFullName(),
              activityDefinitionId: activityDefinition?.id,
              carePlanId: carePlan?.id,
              goalId: goal?.id,
              basedOn: !!basedOn?.length
                ? basedOn?.map((item) => ({ reference: item.reference }))
                : undefined,
              ...(taskBasedOn?.id && { basedOn: [{ reference: `Task/${taskBasedOn?.id}` }] }),
            }
          : {}),
      },
      actionCall: isCreating ? onSuccessfulCreation : onSuccessfulEdit,
      successfulMessage: isCreating
        ? `${i18n('patients.details.tasks.msgCreate', 'crs')}`
        : `${i18n('patients.details.tasks.msgUpdate', 'crs')}`,
    };
  };

  let title = '';

  if (!taskToEdit) {
    title = taskBasedOn
      ? i18n('patients.details.tasks.titleAddSubTasks', 'crs')
      : i18n('patients.details.tasks.titleAddTasks', 'crs');
  } else if (taskBasedOn) {
    title = 'Edit Sub Task';
  } else if (!taskToEdit.id) {
    title = 'Add Task (Copy)';
  } else {
    title = 'Edit Task';
  }

  const updateSelection = (updateKey: string, stateKey: string, resource: any) => {
    if (resource && !resource.id && !resource.resourceType) {
      console.warn('TaskModal: Invalid resource selected', { resource, updateKey, stateKey });
      return;
    }
    const updateObj: any = { isSearchMemberOpen: false, isTaskOwnerOpen: false };
    updateObj[updateKey] = resource;
    updateSelectedMember(updateObj);
    updateState({ [stateKey]: resource });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isSearchMemberOpen || isTaskOwnerOpen ? '94vw' : '70vw',
          paddingX: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <Box display={'flex'} flexDirection={'row'} alignItems={'center'} sx={{ paddingTop: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <Box sx={{ marginLeft: 1 }} display={'flex'} flexDirection={'row'} alignItems={'center'}>
          {[
            taskBasedOn
              ? `List of ${i18n('patients.details.tasks.titleSubTasks', 'crs')}`
              : `List of ${i18n('patients.details.tasks.title', 'crs')}`,
            title,
          ].map((breadcrumbTitle, index) => (
            <BreadCrumbTitle key={breadcrumbTitle} useSeparator={!!index} title={breadcrumbTitle} />
          ))}
        </Box>
      </Box>

      {(carePlan || patient || !!basedOn?.length) && (
        <TaskInfoRibbon
          carePlan={carePlan ?? null}
          patient={patient}
          reference={basedOn?.[0] ?? null}
          taskBasedOn={taskBasedOn}
          configurations={configurations}
          i18n={i18n}
        />
      )}
      {goalToDisplay && <GoalInfoRibbon goal={goalToDisplay} />}

      <Box display="flex" sx={{ mt: 3, gap: 2 }}>
        <Paper
          elevation={0}
          sx={{
            width: '250px',
            minWidth: '250px',
            backgroundColor: '#f4f6f8',
            borderRadius: 1,
            p: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#212b36' }}>
            Task Wizard
          </Typography>
          <Tabs
            orientation="vertical"
            value={activeSection}
            onChange={(_, newValue) => setActiveSection(newValue)}
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                textAlign: 'left',
                minHeight: 'auto',
                padding: '12px 16px',
                marginBottom: '4px',
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 500,
                },
                '&:hover': {
                  backgroundColor: '#e8eaf6',
                },
              },
            }}
          >
            <Tab
              value="quickSetup"
              label={
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: activeSection === 'quickSetup' ? 600 : 400 }}
                    >
                      Quick Setup
                    </Typography>
                    {hasSectionErrors('quickSetup', errors) && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'error.main',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#637381', fontSize: '0.75rem', display: 'block', mt: 0.5 }}
                  >
                    Set basic details (type, priority, dates, etc)
                  </Typography>
                  {hasSectionErrors('quickSetup', errors) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        fontSize: '0.7rem',
                        display: 'block',
                        mt: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Please fill required fields:{' '}
                      {getSectionErrors('quickSetup', errors).join(', ')}
                    </Typography>
                  )}
                </Box>
              }
            />
            <Tab
              value="assignmentIO"
              label={
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: activeSection === 'assignmentIO' ? 600 : 400 }}
                    >
                      Assignment & I/O
                    </Typography>
                    {hasSectionErrors('assignmentIO', errors) && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'error.main',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#637381', fontSize: '0.75rem', display: 'block', mt: 0.5 }}
                  >
                    Manage assignees, inputs and outputs
                  </Typography>
                  {hasSectionErrors('assignmentIO', errors) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        fontSize: '0.7rem',
                        display: 'block',
                        mt: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Please fill required fields:{' '}
                      {getSectionErrors('assignmentIO', errors).join(', ')}
                    </Typography>
                  )}
                </Box>
              }
            />
            <Tab
              value="annotationsComments"
              label={
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: activeSection === 'annotationsComments' ? 600 : 400 }}
                    >
                      Annotations & Comments
                    </Typography>
                    {hasSectionErrors('annotationsComments', errors) && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'error.main',
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#637381', fontSize: '0.75rem', display: 'block', mt: 0.5 }}
                  >
                    Add annotations and comments
                  </Typography>
                  {hasSectionErrors('annotationsComments', errors) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'error.main',
                        fontSize: '0.7rem',
                        display: 'block',
                        mt: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      Please fill required fields:{' '}
                      {getSectionErrors('annotationsComments', errors).join(', ')}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Tabs>
        </Paper>

        <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 'calc(90vh - 200px)' }}>
          <Grid container justifyContent={'space-between'}>
            <Grid item xs={isSearchMemberOpen || isTaskOwnerOpen ? 6 : 12}>
              {activeSection === 'quickSetup' && (
                <>
                  {!!taskToEdit && (
                    <GridSection mt={0}>
                      <GridItem>
                        <TextField
                          fullWidth
                          label={i18n('tasks.taskNo', 'crs')}
                          value={getTaskNumber}
                          type="text"
                          placeholder={i18n('tasks.taskNo', 'crs')}
                          variant="outlined"
                          disabled
                        />
                      </GridItem>
                      <GridItem>
                        <TextField
                          fullWidth
                          label={i18n('patients.details.tasks.requestedBy', 'crs')}
                          value={requestedBy ?? ''}
                          type="text"
                          placeholder={i18n('patients.details.tasks.requestedBy', 'crs')}
                          variant="outlined"
                          disabled
                        />
                      </GridItem>
                    </GridSection>
                  )}

                  <GridSection>
                    <GridItem>
                      <TextField
                        fullWidth
                        label={i18n('patients.details.tasks.organization', 'crs')}
                        value={patient?.getOrganization() ?? ''}
                        type="text"
                        placeholder="Organization"
                        variant="outlined"
                        disabled
                      />
                    </GridItem>

                    <GridItem>
                      <TextField
                        fullWidth
                        label={i18n('patients.details.tasks.patientName', 'crs')}
                        value={patient?.getFullName() ?? ''}
                        type="text"
                        placeholder="Patient Name"
                        variant="outlined"
                        disabled
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem>
                      <Autocomplete
                        disabled={isLoading || !!taskToEdit}
                        value={activityDefinition}
                        fullWidth
                        onChange={(_: React.SyntheticEvent, activityDefinition) => {
                          const priority = getValueSetConceptValue(
                            taskPriorities?.asList?.() ?? [],
                            activityDefinition?.priority
                          );
                          const intent = getValueSetConceptValue(
                            taskIntents?.asList?.() ?? [],
                            getDynamicValueExpressionByPath(
                              activityDefinition?.dynamicValue,
                              'task.intent'
                            )
                          );

                          const status = getValueSetConceptValue(
                            taskStatuses?.asList?.() ?? [],
                            getDynamicValueExpressionByPath(
                              activityDefinition?.dynamicValue,
                              'task.status'
                            )
                          );

                          const taskType = activityDefinition?.code?.coding?.[0] ?? null;
                          let taskStartDate: moment.Moment | null = null;
                          let taskEndDate: moment.Moment | null = null;
                          if (
                            activityDefinition?.timingDuration?.value &&
                            activityDefinition?.timingDuration?.unit
                          ) {
                            taskStartDate = startDate?.isValid?.() ? startDate : moment();
                            const isNegativeDuration = activityDefinition.timingDuration.value < 0;

                            if (!isNegativeDuration) {
                              taskEndDate = taskStartDate
                                .clone()
                                .add(
                                  activityDefinition?.timingDuration?.value,
                                  activityDefinition?.timingDuration?.unit as DurationInputArg2
                                );
                            }
                          }
                          updateState({
                            activityDefinition,
                            ...(status ? { taskStatus: status } : {}),
                            ...(taskStartDate ? { startDate: taskStartDate } : {}),
                            endDate: taskEndDate,
                            ...(taskType ? { taskType: taskType as CodeSystemConcept } : {}),
                            ...(priority ? { taskPriority: priority } : {}),
                            ...(intent ? { taskIntent: intent } : {}),
                            ...(activityDefinition?.description
                              ? {
                                  description:
                                    activityDefinition?.description ??
                                    activityDefinition?.title ??
                                    activityDefinition?.name,
                                }
                              : {}),
                          });
                        }}
                        options={activityDefinitions}
                        getOptionLabel={({ title }: WrappedActivityDefinition) => title as string}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n('patients.details.tasks.activityDefinition', 'crs')}
                            variant="outlined"
                          />
                        )}
                      />
                    </GridItem>

                    <GridItem>
                      <Autocomplete
                        disabled={isLoading}
                        value={taskReason}
                        fullWidth
                        onChange={(_: React.SyntheticEvent, taskReason) => {
                          updateState({ taskReason });
                        }}
                        options={taskReasons?.asList?.() ?? []}
                        getOptionLabel={({ display }: TaskReason) => display ?? ''}
                        renderInput={(params) => (
                          <TextField {...params} label="Reason" variant="outlined" />
                        )}
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem>
                      <Autocomplete
                        disabled={isLoading}
                        value={taskType}
                        fullWidth
                        onChange={(_: React.SyntheticEvent, taskType) => {
                          updateState({ taskType });
                        }}
                        options={(taskTypes?.asList() ?? []) as TaskType[]}
                        getOptionLabel={({ display }: TaskType) => display as string}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n('patients.details.tasks.taskType', 'crs')}
                            variant="outlined"
                          />
                        )}
                      />
                    </GridItem>

                    <GridItem>
                      <Autocomplete
                        disabled={isLoading || disabledStatus}
                        value={taskStatus}
                        fullWidth
                        onChange={(_: React.SyntheticEvent, taskStatus) => {
                          if (taskStatus) updateErrorState({ taskStatus: null });
                          updateState({ taskStatus });
                        }}
                        options={taskStatuses?.asList?.() ?? []}
                        getOptionLabel={({ display }: TaskStatus) => display ?? ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors?.taskStatus}
                            helperText={errors?.taskStatus}
                            label={`${i18n('patients.details.tasks.status', 'crs')}*`}
                            variant="outlined"
                          />
                        )}
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem xs={12}>
                      <Autocomplete
                        disabled={isLoading}
                        value={taskIntent}
                        fullWidth
                        onChange={(_: React.SyntheticEvent, taskIntent) => {
                          if (taskIntent) updateErrorState({ taskIntent: null });
                          updateState({ taskIntent });
                        }}
                        options={taskIntents?.asList?.() ?? []}
                        getOptionLabel={({ display }: TaskIntent) => display ?? ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors?.taskIntent}
                            helperText={errors?.taskIntent}
                            label={`${i18n('patients.details.tasks.intent', 'crs')}*`}
                            variant="outlined"
                          />
                        )}
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem>
                      <SelectSearchMember
                        patient={patient ?? undefined}
                        isLoading={isLoading || (isPatientLoading && !patient)}
                        valueSelected={selectedTaskOwner}
                        labelText={'patients.details.tasks.taskOwner'}
                        options={searchMemberOptions?.taskSelectOwner ?? []}
                        selectedOption={'selectedTaskOwner'}
                        stateKey={'taskOwner'}
                        updateSelectedMember={updateSelectedMember}
                        updateState={updateState}
                      />
                    </GridItem>

                    <GridItem>
                      <SelectSearchMember
                        patient={patient ?? undefined}
                        isLoading={isLoading || (isPatientLoading && !patient)}
                        valueSelected={selectedPerformer}
                        labelText={'patients.details.tasks.performer'}
                        options={searchMemberOptions?.taskSelectDefault ?? []}
                        selectedOption={'selectedPerformer'}
                        stateKey={'performer'}
                        updateSelectedMember={updateSelectedMember}
                        updateState={updateState}
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem>
                      <DatePickerMoment
                        value={startDate}
                        error={errors?.startDate}
                        disabled={isLoading}
                        label={i18n('tasks.startDate', 'crs')}
                        onChange={(value) => {
                          if (value?.isValid?.() || value === null) {
                            updateErrorState({
                              startDate: null,
                            });
                          }
                          updateState({
                            startDate: value,
                          });
                        }}
                      />
                    </GridItem>

                    <GridItem>
                      <DatePickerMoment
                        error={errors?.endDate}
                        value={endDate}
                        disabled={isLoading}
                        label={i18n('tasks.endDate', 'crs')}
                        onChange={(value) => {
                          if (value?.isValid?.() || value === null) {
                            updateErrorState({
                              endDate: null,
                            });
                          }
                          updateState({
                            endDate: value,
                          });
                        }}
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem>
                      <DatePickerMoment
                        value={requestedStart}
                        error={errors?.requestedStart}
                        disabled={isLoading}
                        label={i18n('patients.details.tasks.requestedStart', 'crs')}
                        onChange={(value) => {
                          if (value?.isValid?.() || value === null) {
                            updateErrorState({
                              requestedStart: null,
                            });
                          }
                          updateState({
                            requestedStart: value,
                          });
                        }}
                      />
                    </GridItem>

                    <GridItem>
                      <DatePickerMoment
                        error={errors?.requestedEnd}
                        value={requestedEnd}
                        disabled={isLoading}
                        label={i18n('patients.details.tasks.requestedEnd', 'crs')}
                        onChange={(value) => {
                          if (value?.isValid?.() || value === null) {
                            updateErrorState({
                              requestedEnd: null,
                            });
                          }
                          updateState({
                            requestedEnd: value,
                          });
                        }}
                      />
                    </GridItem>
                  </GridSection>

                  {!!taskToEdit && (
                    <GridSection>
                      <GridItem>
                        <DatePickerMoment
                          disabled
                          label={i18n('patients.details.tasks.authoredOn', 'crs')}
                          value={
                            moment(taskToEdit?.authoredOn).isValid()
                              ? moment(taskToEdit?.authoredOn)
                              : !taskToEdit
                              ? moment()
                              : null
                          }
                          onChange={() => {}}
                        />
                      </GridItem>

                      <GridItem>
                        <DatePickerMoment
                          disabled
                          label={'Last Modified Date'}
                          value={
                            moment(taskToEdit?.lastModified).isValid()
                              ? moment(taskToEdit?.lastModified)
                              : null
                          }
                          onChange={() => {}}
                        />
                      </GridItem>
                    </GridSection>
                  )}
                </>
              )}

              {activeSection === 'assignmentIO' && (
                <>
                  <GridSection>
                    <GridItem hidden={taskFormConfig?.priority?.hide || false}>
                      <Autocomplete
                        disabled={isLoading}
                        value={selectedTaskPriorityOption}
                        fullWidth
                        onChange={(_: React.SyntheticEvent, taskPriority) => {
                          updateState({ taskPriority });

                          setPriorityCode(taskPriority?.code);
                        }}
                        options={taskPrioritiesOptions}
                        getOptionLabel={({ display }: TaskPriority) => display ?? ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={i18n('patients.details.tasks.priority', 'crs')}
                            variant="outlined"
                          />
                        )}
                      />
                    </GridItem>

                    <GridItem xs={taskFormConfig?.priority?.hide ? 12 : undefined}>
                      <SelectSearchMember
                        patient={patient ?? undefined}
                        isLoading={isLoading || (isPatientLoading && !patient)}
                        valueSelected={selectedRequester}
                        labelText={'patients.details.tasks.requester'}
                        options={searchMemberOptions?.taskSelectRequester ?? []}
                        selectedOption={'selectedRequester'}
                        stateKey={'requester'}
                        updateSelectedMember={updateSelectedMember}
                        updateState={updateState}
                      />
                    </GridItem>
                  </GridSection>

                  <GridSection>
                    <GridItem>
                      <SelectSearchMember
                        patient={patient ?? undefined}
                        isLoading={isLoading || (isPatientLoading && !patient)}
                        valueSelected={selectedRequesterPerformer}
                        labelText={'patients.details.tasks.requesterPerformer'}
                        options={searchMemberOptions?.taskSelectDefault ?? []}
                        selectedOption={'selectedRequesterPerformer'}
                        stateKey={'requesterPerformer'}
                        updateSelectedMember={updateSelectedMember}
                        updateState={updateState}
                      />
                    </GridItem>
                  </GridSection>

                  <ExpandableCard
                    title={i18n('patients.details.tasks.inputTitle', 'crs') || 'Inputs'}
                    sxProp={{
                      mt: 2,
                      border: '1px solid',
                      borderColor: '#919eab3d',
                      borderRadius: 1,
                      transition: 'border-color 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <TaskInputComponent
                      task={taskToEdit}
                      taskInput={taskInputs}
                      patient={patient}
                      errors={errors}
                      updateState={updateState}
                      updateErrorState={updateErrorState}
                      i18n={i18n}
                      configurations={{
                        fhir: configurations?.fhir,
                        inputTypeFiles: inputTypeFiles,
                      }}
                    />
                  </ExpandableCard>

                  <ExpandableCard
                    title={i18n('patients.details.tasks.outputTitle', 'crs')}
                    sxProp={{
                      mt: 2,
                      border: '1px solid',
                      borderColor: '#919eab3d',
                      borderRadius: 1,
                      transition: 'border-color 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <TaskOutputComponent
                      task={taskToEdit}
                      taskOutput={taskOutputs}
                      patient={patient}
                      errors={errors}
                      updateState={updateState}
                      updateErrorState={updateErrorState}
                      i18n={i18n}
                      configurations={{
                        fhir: configurations?.fhir,
                        outputTypeFiles: outputTypeFiles,
                      }}
                    />
                  </ExpandableCard>
                </>
              )}

              {activeSection === 'annotationsComments' && (
                <>
                  <GridSection>
                    <TextField
                      fullWidth
                      disabled={isLoading}
                      value={description ?? ''}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const { value } = event.target;
                        if (value.trim().length) updateErrorState({ description: null });
                        updateState({
                          description: event.target.value,
                        });
                      }}
                      error={!!errors.description}
                      helperText={errors.description}
                      label={`${i18n('patients.details.tasks.description', 'crs')}*`}
                      placeholder={`${i18n('patients.details.tasks.description', 'crs')}*`}
                      variant="outlined"
                      multiline
                      rows={4}
                    />
                  </GridSection>

                  <GridSection>
                    <TextField
                      fullWidth
                      disabled={isLoading}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        updateState({
                          comments: event.target.value,
                        });
                      }}
                      value={comments ?? ''}
                      label={i18n('patients.details.tasks.comments', 'crs')}
                      placeholder={i18n('patients.details.tasks.comments', 'crs')}
                      variant="outlined"
                      multiline
                      rows={4}
                    />
                  </GridSection>
                </>
              )}
            </Grid>
            {isSearchMemberOpen && (
              <Grid item xs={5.9}>
                <SearchMember
                  patient={patient as WrappedPatient}
                  onSelectResource={(resource) => {
                    if (typeof inputSelected === 'string' && inputSelected === 'requester') {
                      updateSelection('selectedRequester', 'requester', resource);
                    } else if (typeof inputSelected === 'string' && inputSelected === 'taskOwner') {
                      updateSelection('selectedTaskOwner', 'taskOwner', resource);
                    } else if (
                      typeof inputSelected === 'string' &&
                      inputSelected === 'requesterPerformer'
                    ) {
                      updateSelection('selectedRequesterPerformer', 'requesterPerformer', resource);
                    } else if (typeof inputSelected === 'string' && inputSelected === 'performer') {
                      updateSelection('selectedPerformer', 'performer', resource);
                    }
                  }}
                  onClear={() => {
                    if (typeof inputSelected === 'string' && inputSelected === 'requester') {
                      updateSelection('selectedRequester', 'requester', null);
                      updateState({ requester: null });
                    } else if (typeof inputSelected === 'string' && inputSelected === 'taskOwner') {
                      updateSelection('selectedTaskOwner', 'taskOwner', null);
                      updateState({ taskOwner: null });
                    } else if (
                      typeof inputSelected === 'string' &&
                      inputSelected === 'requesterPerformer'
                    ) {
                      updateSelection('selectedRequesterPerformer', 'requesterPerformer', null);
                      updateState({ requesterPerformer: null });
                    } else if (typeof inputSelected === 'string' && inputSelected === 'performer') {
                      updateSelection('selectedPerformer', 'performer', null);
                    }
                  }}
                  externalResourceType={(() => {
                    const selectedOption =
                      (typeof inputSelected === 'string' && inputSelected === 'requester'
                        ? getExternalResourceType(selectedRequester)
                        : undefined) ??
                      (typeof inputSelected === 'string' && inputSelected === 'taskOwner'
                        ? getExternalResourceType(selectedTaskOwner)
                        : undefined) ??
                      (typeof inputSelected === 'string' && inputSelected === 'requesterPerformer'
                        ? getExternalResourceType(selectedRequesterPerformer)
                        : undefined) ??
                      (typeof inputSelected === 'string' && inputSelected === 'performer'
                        ? getExternalResourceType(selectedPerformer)
                        : undefined);
                    return selectedOption;
                  })()}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      <Box sx={{ paddingY: 2 }} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
        <CancelButton disabled={!!isLoading} onClick={() => onClose?.({}, 'backdropClick')} />
        <LoadingButton
          loading={!!isLoading}
          disabled={!!isLoading}
          onClick={handleOnSave}
          variant="contained"
        >
          {i18n('saveChanges')}
        </LoadingButton>
      </Box>
    </Dialog>
  );
};

const errorsInitialState: ErrorFieldState = {
  taskStatus: null,
  taskIntent: null,
  startDate: null,
  endDate: null,
  description: null,
  taskOutputs: null,
  taskInputs: null,
};

export const getResourceDataFromFhirId = (fhirId: string | undefined) => fhirId?.split('/') ?? [];
export const getTaskOwnerByReference = (
  reference: Reference | undefined
): TaskFormState['taskOwner'] => {
  let taskOwner: TaskFormState['taskOwner'] = null;
  const display = reference?.display ?? '';
  const [resourceType, taskOwnerId] = getResourceDataFromFhirId(reference?.reference);
  if (taskOwnerId) {
    switch (resourceType) {
      case 'Practitioner':
      case 'Patient':
        taskOwner = {
          id: taskOwnerId,
          resourceType,
          getFullName: () => display,
        } as WrappedPractitioner | WrappedPatient;
        break;
      case 'PractitionerRole':
        taskOwner = {
          id: taskOwnerId,
          resourceType,
          getPractitionerName: (_: WrappedPractitioner[]) => display,
        } as WrappedPractitionerRole;
        break;
      case 'CareTeam':
        taskOwner = {
          id: taskOwnerId,
          resourceType,
          name: display,
        } as WrappedCareTeam;
        break;
      case 'Organization':
        taskOwner = {
          id: taskOwnerId,
          resourceType,
          name: display,
        } as WrappedOrganization;
        break;
      case 'RelatedPerson':
        taskOwner = {
          id: taskOwnerId,
          resourceType,
          getFullName: () => display,
        } as WrappedRelatedPerson;
        break;
    }
  }
  return taskOwner;
};

export const getOwnerDisplayLabel = (option: TaskFormState['taskOwner']): string => {
  switch (option?.resourceType) {
    case 'Practitioner':
    case 'Patient':
      return option?.getFullName() as string;
    case 'PractitionerRole':
      return option?.getPractitionerName() as string;
    case 'CareTeam':
      return option?.name as string;
    case 'Organization':
      return option?.name as string;
    case 'RelatedPerson':
      return option?.getFullName() as string;
    default:
      return '';
  }
};

export const getPractitionerFromReference = (
  reference: Reference | undefined
): WrappedPractitioner | null => {
  if (!reference?.reference) return null;
  const [resourceType, practitionerId] = getResourceDataFromFhirId(reference?.reference);
  return {
    id: practitionerId,
    resourceType: resourceType as 'Practitioner',
    getFullName: () => reference?.display ?? '',
  } as WrappedPractitioner;
};

const getInitialState = ({
  taskToEdit,
  taskTypes,
  taskStatuses,
  taskIntents,
  taskPriorities,
  taskReasons,
  activityDefinitions,
  configurations,
}: {
  taskToEdit: WrappedTask | undefined;
  taskTypes: TaskType[] | undefined;
  taskStatuses: TaskStatus[] | undefined;
  taskIntents: TaskIntent[] | undefined;
  taskPriorities: TaskPriority[] | undefined;
  taskReasons: TaskReason[] | undefined;
  activityDefinitions: WrappedActivityDefinition[] | null;
  configurations: any;
}): TaskFormState => {
  const taskType = getValueSetConceptValue(taskTypes ?? [], taskToEdit?.getTaskType?.());
  const taskStatus = getValueSetConceptValue(taskStatuses ?? [], taskToEdit?.status);
  const taskPriority = getValueSetConceptValue(taskPriorities ?? [], taskToEdit?.priority);
  const taskIntent = getValueSetConceptValue(taskIntents ?? [], taskToEdit?.intent);
  const taskReason = getValueSetConceptValue(taskReasons ?? [], taskToEdit?.getReasonCode?.());
  let startDate: moment.Moment | null = moment(taskToEdit?.executionPeriod?.start ?? null);
  let endDate: moment.Moment | null = moment(taskToEdit?.executionPeriod?.end ?? null);
  if (!startDate.isValid()) startDate = !taskToEdit ? moment() : null;
  if (!endDate.isValid()) endDate = null;

  const getExtensionTask = taskToEdit?.extension?.find(
    (e) => e?.url === configurations?.taskExtension
  )?.extension;
  const nestedRequestedPeriod = getExtensionTask?.find((e) => e?.url === 'requestedPeriod');
  const requester = getTaskOwnerByReference(taskToEdit?.requester);
  const taskOwner = getTaskOwnerByReference(taskToEdit?.owner);
  const activityDefinition = activityDefinitions?.find(
    (e) => e?.id === taskToEdit?.instantiatesCanonical?.split('/')?.[1]
  );
  const requesterPerformer = getTaskOwnerByReference(
    getExtensionTask?.find((e) => e?.url === 'requesterPerformer')?.valueReference
  );
  const performer = getTaskOwnerByReference(
    getExtensionTask?.find((e) => e?.url === 'performer')?.valueReference
  );

  const parseDateOnlyFromUTC = (utcDateString: string | null | undefined): moment.Moment | null => {
    if (!utcDateString) return null;
    const utcMoment = moment.utc(utcDateString);
    if (!utcMoment.isValid()) return null;
    return moment(utcMoment.format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day');
  };

  const requestedStart = parseDateOnlyFromUTC(nestedRequestedPeriod?.valuePeriod?.start ?? null);
  const requestedEnd = parseDateOnlyFromUTC(nestedRequestedPeriod?.valuePeriod?.end ?? null);
  const requestedByMetaTag =
    taskToEdit?.meta?.tag?.find((e) => e.code === 'ph-created-by')?.display ?? null;
  const requestedBy = requestedByMetaTag
    ? capitalCase(requestedByMetaTag?.split('@')[0].replace('.', ' '))
    : null;

  const cloneTaskOutputInput = <T extends { type?: any }>(
    items: T[] | null | undefined
  ): T[] | null => {
    if (!items || !Array.isArray(items)) return null;
    return items.map((item) => ({
      ...(item ?? {}),
      type: item?.type ? { ...item.type } : item?.type,
    }));
  };

  return {
    taskType,
    taskStatus,
    taskPriority,
    taskIntent,
    taskReason,
    startDate,
    endDate,
    requester,
    taskOwner,
    description: taskToEdit?.description ?? null,
    comments: taskToEdit?.getComments?.() ?? null,
    isLoading: false,
    activityDefinition: activityDefinition ?? null,
    requesterPerformer,
    performer,
    requestedBy,
    requestedStart,
    requestedEnd,
    taskOutputs: cloneTaskOutputInput(taskToEdit?.output),
    taskInputs: cloneTaskOutputInput(taskToEdit?.input),
  };
};

const getInitialSelectedState = (
  taskToEdit: WrappedTask | null | undefined,
  configurations: any
) => {
  const getExtensionTask = taskToEdit?.extension?.find(
    (e) => e?.url === configurations?.taskExtension
  )?.extension;
  const requesterPerformer = getTaskOwnerByReference(
    getExtensionTask?.find((e) => e?.url === 'requesterPerformer')?.valueReference
  );
  const performer = getTaskOwnerByReference(
    getExtensionTask?.find((e) => e?.url === 'performer')?.valueReference
  );
  return {
    isSearchMemberOpen: false,
    isTaskOwnerOpen: false,
    selectedRequester: taskToEdit ? getTaskOwnerByReference(taskToEdit?.requester) : null,
    selectedTaskOwner: taskToEdit ? getTaskOwnerByReference(taskToEdit?.owner) : null,
    selectedRequesterPerformer: taskToEdit ? requesterPerformer : null,
    selectedPerformer: taskToEdit ? performer : null,
    inputSelected: null,
  };
};

export const getExternalResourceType = (
  option: any
): SearchMemberProps['externalResourceType'] | undefined => {
  if (!isEmpty(option)) {
    const selectedOption = option as TaskSelectedMemberOption;
    return selectedOption?.value as SearchMemberProps['externalResourceType'];
  }
  return undefined;
};

export default TaskModal;
